import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  PaperClipIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import formService from '../services/formService';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  attachments: File[];
}

type FieldName = 'name' | 'email' | 'phone' | 'subject' | 'message';
type FieldErrors = Partial<Record<FieldName, string>>;

const MAX_FILES = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
const MESSAGE_MAX = 2000;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const formatBytes = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Ways to reach the bank, each one actionable rather than plain text. */
const channels = [
  {
    icon: PhoneIcon,
    title: 'Call us',
    lines: [
      { label: '7777', note: 'Local calls', href: '#tel:7777' },
      { label: '+252 1 847777', note: 'International', href: '#tel:+2521847777' },
    ],
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: 'WhatsApp',
    lines: [
      {
        label: '+252 61 3737777',
        note: 'Chat with the call centre',
        href: 'https://wa.me/252613737777',
      },
    ],
  },
  {
    icon: EnvelopeIcon,
    title: 'Email',
    lines: [
      { label: 'callcenter@mybank.so', note: 'We reply during working hours', href: 'mailto:callcenter@mybank.so' },
    ],
  },
  {
    icon: MapPinIcon,
    title: 'Visit a branch',
    lines: [{ label: 'Main Branch, Mogadishu', note: 'Somalia', href: null }],
  },
  {
    icon: ClockIcon,
    title: 'Opening hours',
    lines: [{ label: 'Saturday – Thursday', note: '08:00 – 16:30', href: null }],
  },
];

const socials = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/MyBankLTD/',
    path: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z',
  },
  {
    name: 'Twitter',
    href: 'https://x.com/mybankltd?s=21',
    path: 'M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z',
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/mybankltd/',
    path: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z',
  },
];

export default function Contact({ isHomePage }: { isHomePage: boolean }) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    attachments: [],
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const validateField = (field: FieldName, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Please tell us your name.';
        if (value.trim().length < 2) return 'That name looks too short.';
        return undefined;
      case 'email':
        if (!value.trim()) return 'We need an email address to reply to.';
        if (!EMAIL_PATTERN.test(value.trim())) return 'Check the email address — it looks incomplete.';
        return undefined;
      case 'phone':
        if (value && value.replace(/[^\d]/g, '').length < 7) return 'Check the phone number.';
        return undefined;
      case 'subject':
        if (!value.trim()) return 'Add a short subject so we can route your message.';
        return undefined;
      case 'message':
        if (!value.trim()) return 'Please write your message.';
        if (value.trim().length < 10) return 'Add a little more detail so we can help.';
        return undefined;
      default:
        return undefined;
    }
  };

  const validateAll = (): FieldErrors => {
    const next: FieldErrors = {};
    (['name', 'email', 'phone', 'subject', 'message'] as FieldName[]).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) next[field] = error;
    });
    return next;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name as FieldName]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name as FieldName, value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name as FieldName, value) }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const incoming = Array.from(e.target.files);

    const tooBig = incoming.filter((file) => file.size > MAX_FILE_BYTES);
    if (tooBig.length) {
      toast.error(`${tooBig[0].name} is larger than 10 MB.`);
    }

    const sized = incoming.filter((file) => file.size <= MAX_FILE_BYTES);
    if (formData.attachments.length + sized.length > MAX_FILES) {
      toast.error(t('contact.maxFiles'));
      e.target.value = '';
      return;
    }

    setFormData((prev) => ({ ...prev, attachments: [...prev.attachments, ...sized] }));
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nextErrors = validateAll();
    setErrors(nextErrors);
    setTouched({ name: true, email: true, phone: true, subject: true, message: true });

    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      const field = formRef.current?.querySelector<HTMLElement>(`[name="${firstInvalid}"]`);
      field?.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      await formService.submitContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        attachments: formData.attachments,
      });

      toast.success(`📧 ${t('contact.messageSent')}`, { duration: 5000, icon: '✉️' });
      setIsSent(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', attachments: [] });
      setTouched({});
      setErrors({});

      setTimeout(() => {
        toast.success(`📧 ${t('contact.checkEmail')}`, { duration: 4000 });
      }, 2000);
    } catch (error: any) {
      console.error('Contact form error:', error);
      toast.error(
        error?.message ||
          'Error sending message. Please try again or contact us directly at callcenter@mybank.so'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldClass = (field: FieldName) =>
    `h-12 w-full rounded-xl border bg-white px-4 text-[0.95rem] text-brand-navy placeholder:text-secondary-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1 ${
      errors[field]
        ? 'border-red-400 focus-visible:ring-red-400'
        : 'border-brand-line hover:border-secondary-300'
    }`;

  const messageLength = formData.message.length;
  const remaining = useMemo(() => MESSAGE_MAX - messageLength, [messageLength]);

  const RequiredMark = () => (
    <span className="text-red-500" aria-hidden="true">
      *
    </span>
  );

  const FieldError = ({ field }: { field: FieldName }) =>
    errors[field] ? (
      <p id={`${field}-error`} role="alert" className="mt-1.5 text-xs font-medium text-red-600">
        {errors[field]}
      </p>
    ) : null;

  return (
    <div className="bg-white">
      {/* Page lede — hidden when the form is embedded on the homepage */}
      {!isHomePage && (
        <header className="mx-auto w-full max-w-[1200px] px-5 pb-2 pt-12 sm:px-6 lg:px-8 lg:pt-16">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-12">
            <h1 className="text-balance text-[2.25rem] font-bold leading-[1.1] tracking-tight text-brand-navy sm:text-[2.75rem] lg:col-span-5 lg:text-[3.25rem]">
              {t('contact.letsStartA')}{' '}
              <span className="text-brand-blue">{t('contact.conversation')}</span>
            </h1>
            <p className="text-pretty text-lg leading-relaxed text-secondary-600 lg:col-span-7 lg:pt-3 lg:text-xl">
              {t('contact.haveQuestions')}
            </p>
          </div>
        </header>
      )}

      <section className={isHomePage ? 'py-16' : 'py-12 lg:py-16'}>
        <div className="mx-auto w-full max-w-[1200px] px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            {/* Channels */}
            <div className="lg:col-span-5">
              <h2 className="text-2xl font-bold tracking-tight text-brand-navy sm:text-[1.75rem]">
                Get in touch
              </h2>
              <p className="mt-3 max-w-[46ch] text-[1.0625rem] leading-relaxed text-secondary-600">
                Choose whichever way suits you. Our call centre answers from 08:00 to 16:30,
                Saturday to Thursday.
              </p>

              <dl className="mt-8 divide-y divide-brand-line border-y border-brand-line">
                {channels.map((channel) => {
                  const Icon = channel.icon;
                  return (
                    <div key={channel.title} className="flex gap-4 py-5">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-blue">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-secondary-500">
                          {channel.title}
                        </dt>
                        {channel.lines.map((line) => (
                          <dd key={line.label} className="mt-1">
                            {line.href ? (
                              <a
                                href={line.href}
                                // {...(line.href.startsWith('http')
                                //   ? { target: '_blank', rel: 'noopener noreferrer' }
                                //   : {})}
                                className="text-[0.975rem] font-semibold text-brand-navy underline-offset-4 transition-colors hover:text-brand-blue hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
                              >
                                {line.label}
                              </a>
                            ) : (
                              <span className="text-[0.975rem] font-semibold text-brand-navy">
                                {line.label}
                              </span>
                            )}
                            <span className="ml-2 text-sm text-secondary-500">{line.note}</span>
                          </dd>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </dl>

              {/* Social */}
              <div className="mt-8 flex items-center gap-4">
                <span className="text-sm text-secondary-500">Follow MyBank</span>
                <ul className="flex items-center gap-2">
                  {socials.map((social) => (
                    <li key={social.name}>
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-line text-brand-navy transition-colors hover:border-brand-blue/40 hover:bg-brand-tint hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
                      >
                        <span className="sr-only">{social.name} (opens in a new tab)</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d={social.path} />
                        </svg>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_1px_2px_rgba(16,24,40,0.04)] sm:p-8">
                {isSent ? (
                  <div role="status" className="py-10 text-center">
                    <CheckCircleIcon className="mx-auto h-12 w-12 text-brand-blue" aria-hidden="true" />
                    <h2 className="mt-4 text-xl font-bold text-brand-navy">
                      {t('contact.messageSent')}
                    </h2>
                    <p className="mx-auto mt-2 max-w-sm text-[0.975rem] leading-relaxed text-secondary-600">
                      Our team will get back to you during working hours. For anything urgent, call
                      7777.
                    </p>
                    <button
                      type="button"
                      onClick={() => setIsSent(false)}
                      className="mt-6 inline-flex h-11 items-center rounded-full border border-brand-navy px-5 text-sm font-semibold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-brand-navy sm:text-2xl">
                      {t('contact.title')}
                    </h2>
                    <p className="mt-2 text-[0.975rem] text-secondary-600">
                      {t('contact.fillOutForm')}
                    </p>

                    <form ref={formRef} onSubmit={handleSubmit} noValidate className="mt-7 space-y-5">
                      <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-brand-navy">
                            {t('contact.name')} <RequiredMark />
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-required="true"
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={errors.name ? 'name-error' : undefined}
                            className={`mt-1.5 ${fieldClass('name')}`}
                            placeholder="Amina Yusuf"
                            autoComplete="name"
                          />
                          <FieldError field="name" />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-brand-navy">
                            {t('contact.email')} <RequiredMark />
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-required="true"
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={errors.email ? 'email-error' : undefined}
                            className={`mt-1.5 ${fieldClass('email')}`}
                            placeholder="you@example.com"
                            autoComplete="email"
                          />
                          <FieldError field="email" />
                        </div>
                      </div>

                      <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-brand-navy">
                            {t('contact.phone')}{' '}
                            <span className="font-normal text-secondary-400">(optional)</span>
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-invalid={Boolean(errors.phone)}
                            aria-describedby={errors.phone ? 'phone-error' : undefined}
                            className={`mt-1.5 ${fieldClass('phone')}`}
                            placeholder="+252 61 3737777"
                            autoComplete="tel"
                          />
                          <FieldError field="phone" />
                        </div>

                        <div>
                          <label htmlFor="subject" className="block text-sm font-medium text-brand-navy">
                            {t('contact.subject')} <RequiredMark />
                          </label>
                          <input
                            type="text"
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-required="true"
                            aria-invalid={Boolean(errors.subject)}
                            aria-describedby={errors.subject ? 'subject-error' : undefined}
                            className={`mt-1.5 ${fieldClass('subject')}`}
                            placeholder="Opening a business account"
                          />
                          <FieldError field="subject" />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-baseline justify-between gap-3">
                          <label htmlFor="message" className="block text-sm font-medium text-brand-navy">
                            {t('contact.message')} <RequiredMark />
                          </label>
                          <span
                            className={`text-xs tabular-nums ${
                              remaining < 100 ? 'text-red-600' : 'text-secondary-400'
                            }`}
                          >
                            {messageLength}/{MESSAGE_MAX}
                          </span>
                        </div>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          rows={5}
                          maxLength={MESSAGE_MAX}
                          aria-required="true"
                          aria-invalid={Boolean(errors.message)}
                          aria-describedby={errors.message ? 'message-error' : undefined}
                          className={`mt-1.5 w-full rounded-xl border bg-white px-4 py-3 text-[0.95rem] leading-relaxed text-brand-navy placeholder:text-secondary-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1 ${
                            errors.message
                              ? 'border-red-400 focus-visible:ring-red-400'
                              : 'border-brand-line hover:border-secondary-300'
                          }`}
                          placeholder="Tell us what you need help with…"
                        />
                        <FieldError field="message" />
                      </div>

                      {/* Attachments */}
                      <div>
                        <span className="block text-sm font-medium text-brand-navy">
                          {t('contact.attachments')}{' '}
                          <span className="font-normal text-secondary-400">(optional)</span>
                        </span>
                        <input
                          type="file"
                          id="file-upload"
                          onChange={handleFileChange}
                          className="sr-only"
                          multiple
                          accept={ACCEPTED_TYPES.join(',')}
                          disabled={formData.attachments.length >= MAX_FILES}
                        />
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                          <label
                            htmlFor="file-upload"
                            className={`inline-flex h-11 items-center gap-2 rounded-full border border-brand-line px-4 text-sm font-semibold transition-colors focus-within:ring-2 focus-within:ring-brand-blue ${
                              formData.attachments.length >= MAX_FILES
                                ? 'cursor-not-allowed text-secondary-400'
                                : 'cursor-pointer text-brand-navy hover:border-brand-blue/40 hover:bg-brand-tint'
                            }`}
                          >
                            <PaperClipIcon className="h-4 w-4" aria-hidden="true" />
                            Attach files
                          </label>
                          <p className="text-xs text-secondary-500">
                            Up to {MAX_FILES} files · PDF, DOC, DOCX, JPG, PNG · 10 MB each
                          </p>
                        </div>

                        {formData.attachments.length > 0 && (
                          <ul className="mt-3 space-y-2" aria-live="polite">
                            {formData.attachments.map((file, index) => (
                              <li
                                key={`${file.name}-${index}`}
                                className="flex items-center justify-between gap-3 rounded-xl border border-brand-line bg-secondary-50 px-3 py-2.5"
                              >
                                <span className="flex min-w-0 items-center gap-2.5">
                                  <DocumentIcon
                                    className="h-4 w-4 flex-shrink-0 text-brand-blue"
                                    aria-hidden="true"
                                  />
                                  <span className="truncate text-sm text-brand-navy">{file.name}</span>
                                  <span className="flex-shrink-0 text-xs tabular-nums text-secondary-500">
                                    {formatBytes(file.size)}
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-secondary-500 transition-colors hover:bg-white hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
                                >
                                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                  <span className="sr-only">Remove {file.name}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-brand-line pt-5">
                        <p className="text-xs text-secondary-500">
                          Never send account numbers, PINs or passwords by email.
                        </p>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="inline-flex h-12 items-center gap-2 rounded-full bg-brand-navy px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
                        >
                          {isSubmitting && (
                            <ArrowPathIcon
                              className="h-4 w-4 animate-spin motion-reduce:animate-none"
                              aria-hidden="true"
                            />
                          )}
                          {isSubmitting ? 'Sending…' : t('contact.send')}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
